import type { GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import React from 'react';
import { fetchBlogPost, fetchBlogPosts } from 'src/actions/blogActions';
import { searchForAllCountriesStaticProps } from 'src/actions/searchActions';
import { Card, Divider, Stack } from 'src/components/core';
import { TextLink } from 'src/components/core/textLink.component';
import { DocumentAuthorCard } from 'src/components/document/authorCard.component';
import { DocumentBody } from 'src/components/document/body.component';
import { DocumentCoverImage } from 'src/components/document/coverImage.component';
import { DocumentHeader } from 'src/components/document/header.component';
import { PageLayout } from 'src/components/layout/pageLayout.component';
import { StickyAside } from 'src/components/layout/stickyAside.component';
import type { PathsTypes } from 'src/types/render-types';
import type { DataWorldChartTypes } from 'src/types/types';
import type { BlogPage } from '../../types/blogPage';

interface BlogSlugProps {
  post: BlogPage;
  countries: DataWorldChartTypes[];
}

const BlogSlug: NextPage<BlogSlugProps> = ({ post, countries }) => {
  const router = useRouter();
  if (!router.isFallback && !post?.attributes?.slug) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <PageLayout
      title={post?.attributes?.title}
      description={
        post?.attributes?.short_description
          ? post?.attributes?.short_description
          : ''
      }
      countries={countries}
    >
      <div className="relative grid grid-cols-7 gap-4 pb-4">
        <Card
          component="main"
          className="col-span-7 px-0.5 py-4 sm:col-span-5 sm:px-10 sm:py-6"
        >
          <Stack direction="vertical" gap={['space-y-5', 'sm:space-y-6']}>
            {!!post?.attributes?.image?.data?.attributes?.url && (
              <DocumentCoverImage
                alt={post?.attributes?.title}
                src={post?.attributes?.image?.data?.attributes?.url}
              />
            )}
            <DocumentHeader
              document={post}
              subtitle={
                post?.attributes?.short_description
                  ? post?.attributes?.short_description
                  : ''
              }
            />
            <Divider />
            <DocumentBody html={post?.attributes?.content} />
          </Stack>
        </Card>
        <StickyAside className="col-span-7 sm:col-span-2">
          {post?.attributes && <DocumentAuthorCard author={post?.attributes} />}
          <div className="mt-2">
            <TextLink
              type="route"
              href="/founders-thoughts"
              className="text-sm"
            >
              ← Back to founder's thoughts
            </TextLink>
          </div>
        </StickyAside>
      </div>
    </PageLayout>
  );
};

export default BlogSlug;

export const getStaticProps: GetStaticProps<
  Record<string, unknown>,
  { slug: string; locale: string }
> = async ({ params, locale }) => {
  const slug = params?.slug ?? '';
  const blogPosts = await fetchBlogPosts();
  const blogPostId = blogPosts?.data.find((post) => {
    return post?.attributes.slug === slug;
  })?.id;

  if (!blogPostId) {
    throw new Error('Error fetching post or countries');
  }

  try {
    const post = await fetchBlogPost(blogPostId.toString());
    const countries = await searchForAllCountriesStaticProps();

    if (!post || !countries) {
      throw new Error('Error fetching post or countries');
    }

    return {
      props: {
        ...(await serverSideTranslations(locale || 'en', [
          'common',
          'about',
          'partners',
          'navbar',
          'new-homepage',
          'new-navbar',
          'new-footer',
        ])),
        post: post || {},
        countries: countries || [],
      },
      revalidate: 3600,
    };
  } catch (error) {
    console.log(error);
    return {
      props: {},
    };
  }
};

export const getStaticPaths = async () => {
  const blogPosts = await fetchBlogPosts();

  const slugs: string[] = [];

  blogPosts?.data.forEach((post) => {
    slugs?.push(post?.attributes.slug?.toString());
  });

  const paths: PathsTypes[] = slugs?.map((slugName: string) => ({
    params: { slug: slugName?.toString() },
  }));

  return { paths: paths, fallback: 'blocking' };
};
